import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  Divider,
  ToggleButtonGroup,
} from '@mui/joy';
import {
  FloppyDisk as SaveIcon,
  Eye as PreviewIcon,
  UploadSimple as PublishIcon,
  ArrowLeft as ArrowBackIcon,
  Columns as SplitIcon,
  Rows as FullIcon,
  PaintBrush as VisualIcon,
  Code as ClassicIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ContentTabV2 } from './editor/ContentTabV2';
import { SEOTab } from './editor/SEOTab';
import { SettingsTab } from './editor/SettingsTab';
import { SectionEditor } from './editor/SectionEditor';
import { api } from '../../lib/api';
import { getSignedPreviewUrl } from '../../lib/previewUrl';
import { LivePreview } from '../../components/cms/LivePreview';
import { SplitView } from '../../components/cms/SplitView';
import { VisualEditor } from '../../components/cms/VisualEditor';
import { useUndoRedo } from '../../hooks/useUndoRedo';
import { UndoRedoControls } from '../../components/cms/UndoRedoControls';
// import { AutoSaveIndicator } from '../../components/cms/AutoSaveIndicator';
// import { useAutoSave } from '../../hooks/useAutoSave';
import { KeyboardShortcuts, useKeyboardShortcuts } from '../../components/cms/KeyboardShortcuts';
import { AIChatAssistant } from '../../components/cms/AIChatAssistant';
import { SuccessMessage } from '../../components/cms/SuccessMessage';

interface PageData {
  id: number;
  site: number;
  site_name: string;
  parent: number | null;
  title: string;
  slug: string;
  template_key: string;
  status: 'draft' | 'published';
  sections_draft: any[];
  seo_draft: {
    title?: string;
    description?: string;
    keywords?: string;
    og_title?: string;
    og_description?: string;
    og_image?: string;
    twitter_title?: string;
    twitter_description?: string;
    twitter_image?: string;
    canonical_url?: string;
  };
  settings?: any;
  ai_generation_settings?: {
    tone?: string;
    style?: string;
    keywords?: string[];
  };
  content_brief?: string;
  order?: number;
  version_counter: number;
  published_version?: number;
  created_at: string;
  updated_at: string;
}

interface SectionManifest {
  version: string;
  name: string;
  description: string;
  categories: Record<string, any>;
  sections: Record<string, any>;
}

export const PageEditor: React.FC = () => {
  const { siteId, pageId } = useParams<{ siteId: string; pageId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pageData, setPageData] = useState<PageData | null>(null);
  const [autoSaveMessage, setAutoSaveMessage] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const isAutoSavingRef = useRef(false);
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });
  const [editorMode, setEditorMode] = useState<'classic' | 'visual'>('classic');
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  
  // Check if we're creating a new page - check URL path since pageId might be undefined
  const isNewPage = window.location.pathname.includes('/pages/new') || pageId === 'new';
  
  console.log('PageEditor: siteId=', siteId, 'pageId=', pageId, 'isNewPage=', isNewPage, 'pathname=', window.location.pathname);

  // Fetch page data (only if editing existing page)
  const {
    data: page,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['cms-page', pageId],
    queryFn: async () => {
      const response = await api.get<PageData>(`/api/cms/pages/${pageId}/`);
      return response.data || response;
    },
    enabled: !!pageId && !isNewPage,
  });

  // Fetch universal section manifest
  const { data: manifest } = useQuery({
    queryKey: ['section-manifest-v2'],
    queryFn: async () => {
      const response = await api.get<SectionManifest>('/api/cms/sections/manifest/');
      return response.data || response;
    },
    enabled: !!(page?.site || siteId),
  });

  // Fetch site data for preview domain
  const { data: site } = useQuery({
    queryKey: ['cms-site', siteId],
    queryFn: async () => {
      const response = await api.get(`/api/cms/sites/${siteId}/`);
      return response.data || response;
    },
    enabled: !!siteId,
  });

  useEffect(() => {
    console.log('PageEditor useEffect: page=', page, 'isNewPage=', isNewPage, 'siteId=', siteId);
    if (page) {
      // Ensure all required fields are present
      setPageData({
        ...page,
        parent: page.parent ?? null,
        title: page.title ?? '',
      });
    } else if (isNewPage && siteId) {
      console.log('PageEditor: Initializing new page data');
      // Initialize with default data for new page
      // Generate a default slug for new pages
      const timestamp = Date.now();
      const defaultSlug = `page-${timestamp}`;
      
      setPageData({
        id: 0,
        site: parseInt(siteId),
        site_name: '',
        parent: null,
        title: '',
        slug: defaultSlug,
        template_key: 'generic',
        status: 'draft',
        sections_draft: [],
        seo_draft: {
          title: '',
          description: '',
        },
        version_counter: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as PageData);
    }
  }, [page, isNewPage, siteId]);

  // Save draft mutation
  const saveMutation = useMutation({
    mutationFn: async (data: Partial<PageData>) => {
      if (isNewPage) {
        // Create new page
        return api.post(`/api/cms/pages/`, { ...data, site: parseInt(siteId || '0') });
      } else {
        // Update existing page
        return api.patch(`/api/cms/pages/${pageId}/`, data);
      }
    },
    onSuccess: (response: any) => {
      const responseData = response?.data || response;
      if (isNewPage && responseData?.id) {
        // Navigate to the edit page for the newly created page
        navigate(`/cms/sites/${siteId}/pages/${responseData.id}`, { replace: true });
      }
      queryClient.invalidateQueries({ queryKey: ['cms-page', pageId] });
      queryClient.invalidateQueries({ queryKey: ['cms-pages', siteId] });
      setHasUnsavedChanges(false);
      if (isAutoSavingRef.current) {
        setAutoSaveMessage('All changes saved');
        isAutoSavingRef.current = false;
      } else {
        // Show success message for manual saves
        setSuccessMessage({
          title: isNewPage ? 'Page Created!' : 'Draft Saved!',
          message: isNewPage ? 'Your new page has been created successfully.' : 'Your changes have been saved as a draft.'
        });
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    },
    onError: (error: any) => {
      console.error('Save error:', error);
      let errorMessage = 'Failed to save page. Please try again.';
      
      // Parse validation errors from the response
      if (error.response?.data) {
        const data = error.response.data;
        const errors: string[] = [];
        
        // Handle field-specific errors
        Object.entries(data).forEach(([field, value]) => {
          if (Array.isArray(value)) {
            errors.push(`${field}: ${value.join(', ')}`);
          } else if (typeof value === 'string') {
            errors.push(`${field}: ${value}`);
          }
        });
        
        if (errors.length > 0) {
          errorMessage = `Validation errors:\n${errors.join('\n')}`;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        }
      }
      
      setSuccessMessage({
        title: 'Save Failed',
        message: errorMessage
      });
      setShowSuccessMessage(true);
      isAutoSavingRef.current = false;
    },
  });

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/api/cms/pages/${pageId}/publish/`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-page', pageId] });
      setHasUnsavedChanges(false);
      setSuccessMessage({
        title: 'Page Published!',
        message: 'Your page is now live and visible to visitors.'
      });
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    },
  });

  const handleSave = async (auto = false) => {
    if (!pageData) return;
    if (auto) {
      isAutoSavingRef.current = true;
    }
    
    // Build save payload, excluding read-only fields
    const saveData: any = {
      sections_draft: pageData.sections_draft,
      seo_draft: pageData.seo_draft,
      template_key: pageData.template_key,
      slug: pageData.slug,
      title: pageData.title || pageData.seo_draft?.title || '',
      ai_generation_settings: pageData.ai_generation_settings,
      content_brief: pageData.content_brief,
      order: pageData.order || 0,  // Always include order with default value
    };
    
    // Only include parent if it's set (can be null or a number)
    if (pageData.parent !== undefined) {
      saveData.parent = pageData.parent;
    }
    
    await saveMutation.mutateAsync(saveData);
  };

  const handlePublish = async () => {
    if (!pageData) return;
    
    // Save first if there are unsaved changes
    if (hasUnsavedChanges) {
      await handleSave();
    }
    
    // Then publish
    await publishMutation.mutateAsync();
  };

  const handlePreview = async () => {
    if (!pageData || !siteId) return;
    // Get signed preview URL from backend
    const previewUrl = await getSignedPreviewUrl(siteId, pageData.slug);
    window.open(previewUrl, '_blank', 'noopener');
  };

  // Undo/Redo functionality
  const {
    state: undoState,
    setState: addToHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    historyInfo,
  } = useUndoRedo<any>(pageData || {
    sections_draft: [],
    seo_draft: {},
    settings: {},
  });

  // Update pageData when undo/redo is used
  useEffect(() => {
    if (undoState && pageData && !isNewPage) {
      setPageData({
        ...pageData,
        sections_draft: undoState.sections_draft || pageData.sections_draft,
        seo_draft: undoState.seo_draft || pageData.seo_draft,
        settings: undoState.settings || pageData.settings,
      });
      setHasUnsavedChanges(true);
      setRefreshTrigger(prev => prev + 1);
    }
  }, [undoState]);

  // Add to history when pageData changes
  useEffect(() => {
    if (pageData && !isNewPage) {
      addToHistory({
        sections_draft: pageData.sections_draft,
        seo_draft: pageData.seo_draft,
        settings: pageData.settings || {},
      });
    }
  }, [pageData?.sections_draft, pageData?.seo_draft]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    'ctrl+s': () => handleSave(false),
    'ctrl+shift+p': () => handlePublish(),
    'ctrl+p': () => handlePreview(),
    '?': () => setShowKeyboardShortcuts(true),
    'ctrl+shift+l': () => setShowLivePreview(!showLivePreview),
  });

  const handleDataChange = (field: string, value: any) => {
    if (!pageData) return;
    
    const updatedData = { ...pageData };
    
    if (field.startsWith('seo.')) {
      const seoField = field.replace('seo.', '');
      updatedData.seo_draft = {
        ...updatedData.seo_draft,
        [seoField]: value,
      };
    } else if (field === 'sections') {
      updatedData.sections_draft = value;
    } else {
      (updatedData as any)[field] = value;
    }
    
    setPageData(updatedData);
    setHasUnsavedChanges(true);
    setAutoSaveMessage('');
  };

  // Auto-save functionality for Visual Editor real-time preview
  // Debounced save after 500ms of inactivity
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Don't auto-save for new pages (need title first) or if already saving
    if (!hasUnsavedChanges || !pageData || isNewPage || saveMutation.isPending) return;

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Debounced save after 500ms
    autoSaveTimerRef.current = setTimeout(() => {
      handleSave(true);
    }, 500);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, pageData?.sections_draft, saveMutation.isPending, isNewPage]);

  if (isLoading && !isNewPage) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  console.log('PageEditor render: pageData=', pageData, 'error=', error, 'isNewPage=', isNewPage, 'isLoading=', isLoading);
  
  if (!isNewPage && (error || !pageData)) {
    console.log('PageEditor: Showing error - not new page and has error or no data');
    return (
      <Box sx={{ p: 2 }}>
        <Alert color="danger">Failed to load page</Alert>
      </Box>
    );
  }
  
  if (!pageData) {
    // For new pages, show loading while initializing
    if (isNewPage) {
      console.log('PageEditor: Showing loading spinner for new page');
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    console.log('PageEditor: Returning null - edge case');
    return null; // Should not happen, but safety check
  }

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.surface',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="plain"
            startDecorator={<ArrowBackIcon />}
            onClick={() => navigate(`/cms/sites/${siteId}/pages`)}
          >
            Back to Pages
          </Button>
          
          <Breadcrumbs separator="›">
            <Link onClick={() => navigate('/cms/sites')}>Sites</Link>
            <Link onClick={() => navigate(`/cms/sites/${siteId}/pages`)}>
              {pageData.site_name}
            </Link>
            <Typography>{isNewPage ? 'New Page' : (pageData.seo_draft?.title || pageData.slug)}</Typography>
          </Breadcrumbs>

          {pageData.status === 'published' && (
            <Chip color="success" size="sm">
              Published v{pageData.published_version}
            </Chip>
          )}
          
          {hasUnsavedChanges && (
            <Chip color="warning" size="sm">
              Unsaved Changes
            </Chip>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Auto-save disabled to prevent continuous saving */}
          {/* <AutoSaveIndicator status={autoSaveStatus || 'idle'} /> */}
          
          <UndoRedoControls
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={undo}
            onRedo={redo}
            historyInfo={historyInfo}
          />
          
          <Divider orientation="vertical" />

          {/* Editor Mode Toggle */}
          <ToggleButtonGroup
            value={editorMode}
            onChange={(event, value) => value && setEditorMode(value)}
            size="sm"
          >
            <Button value="classic" startDecorator={<ClassicIcon />}>
              Classic
            </Button>
            <Button value="visual" startDecorator={<VisualIcon />}>
              Visual
            </Button>
          </ToggleButtonGroup>

          {editorMode === 'classic' && (
            <Button
              variant={showLivePreview ? 'soft' : 'outlined'}
              color={showLivePreview ? 'primary' : 'neutral'}
              startDecorator={showLivePreview ? <FullIcon /> : <SplitIcon />}
              onClick={() => setShowLivePreview(!showLivePreview)}
            >
              {showLivePreview ? 'Editor Only' : 'Live Preview'}
            </Button>
          )}
          
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<PreviewIcon />}
            onClick={handlePreview}
          >
            Preview
          </Button>

          <Button
            variant="solid"
            color="primary"
            startDecorator={<SaveIcon />}
            onClick={() => handleSave()}
            loading={saveMutation.isPending}
            disabled={!hasUnsavedChanges}
          >
            Save Draft
          </Button>

          <Button
            variant="solid"
            color="success"
            startDecorator={<PublishIcon />}
            onClick={handlePublish}
            loading={publishMutation.isPending}
            disabled={isNewPage}
          >
            Publish
          </Button>
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {editorMode === 'visual' && site && manifest ? (
          /* Visual Editor Mode */
          <VisualEditor
            siteId={siteId || ''}
            pageData={{
              id: pageData.id,
              slug: pageData.slug,
              sections_draft: pageData.sections_draft || [],
              seo_draft: pageData.seo_draft || {},
              template_key: pageData.template_key,
            }}
            siteData={{
              domain: (site as any)?.domain || '',
              theme_key: (site as any)?.theme_key || 'atlas',
              settings: (site as any)?.settings || {},
            }}
            manifest={manifest?.sections || {}}
            onSectionsChange={(sections) => handleDataChange('sections', sections)}
            onSectionSelect={(index) => {
              // Optionally track selected section for external state
            }}
            onEditSection={(index) => {
              setEditingSectionIndex(index);
            }}
          />
        ) : showLivePreview && site ? (
          <SplitView
            left={
              <Tabs
                value={activeTab}
                onChange={(event, value) => setActiveTab(value as number)}
                sx={{ height: '100%' }}
              >
                <TabList>
                  <Tab>Content</Tab>
                  <Tab>SEO</Tab>
                  <Tab>Settings</Tab>
                </TabList>

                <TabPanel value={0} sx={{ height: 'calc(100% - 48px)', overflow: 'auto' }}>
                  <ContentTabV2
                    sections={pageData.sections_draft || []}
                    onSectionsChange={(sections) => handleDataChange('sections', sections)}
                    aiSettings={pageData.ai_generation_settings}
                    contentBrief={pageData.content_brief}
                    siteId={pageData.site}
                    pageType={pageData.slug === 'index' ? 'home' : pageData.slug}
                  />
                </TabPanel>

                <TabPanel value={1} sx={{ height: 'calc(100% - 48px)', overflow: 'auto' }}>
                  <SEOTab
                    seo={pageData.seo_draft || {}}
                    onChange={(field, value) => handleDataChange(`seo.${field}`, value)}
                    aiSettings={pageData.ai_generation_settings}
                  />
                </TabPanel>

                <TabPanel value={2} sx={{ height: 'calc(100% - 48px)', overflow: 'auto' }}>
                  <SettingsTab
                    slug={pageData.slug}
                    template={pageData.template_key}
                    aiSettings={pageData.ai_generation_settings}
                    contentBrief={pageData.content_brief}
                    manifest={manifest}
                    onChange={handleDataChange}
                    siteId={pageData.site || parseInt(siteId || '0')}
                    pageId={pageData.id}
                    currentParent={pageData.parent}
                  />
                </TabPanel>
              </Tabs>
            }
            right={
              <LivePreview
                siteId={siteId || ''}
                pageData={pageData}
                siteData={{
                  domain: (site as any)?.domain || '',
                  theme_key: (site as any)?.theme_key || 'atlas',
                  settings: (site as any)?.settings || {},
                }}
                refreshTrigger={refreshTrigger}
              />
            }
            defaultSplit={60}
          />
        ) : (
          <Tabs
            value={activeTab}
            onChange={(event, value) => setActiveTab(value as number)}
            sx={{ height: '100%' }}
          >
            <TabList>
              <Tab>Content</Tab>
              <Tab>SEO</Tab>
              <Tab>Settings</Tab>
            </TabList>

            <TabPanel value={0} sx={{ height: 'calc(100% - 48px)', overflow: 'auto' }}>
              <ContentTabV2
                sections={pageData.sections_draft || []}
                onSectionsChange={(sections) => handleDataChange('sections', sections)}
                aiSettings={pageData.ai_generation_settings}
                contentBrief={pageData.content_brief}
                siteId={pageData.site}
                pageType={pageData.slug === 'index' ? 'home' : pageData.slug}
              />
            </TabPanel>

            <TabPanel value={1} sx={{ height: 'calc(100% - 48px)', overflow: 'auto' }}>
              <SEOTab
                seo={pageData.seo_draft || {}}
                onChange={(field, value) => handleDataChange(`seo.${field}`, value)}
                aiSettings={pageData.ai_generation_settings}
              />
            </TabPanel>

            <TabPanel value={2} sx={{ height: 'calc(100% - 48px)', overflow: 'auto' }}>
              <SettingsTab
                slug={pageData.slug}
                template={pageData.template_key}
                aiSettings={pageData.ai_generation_settings}
                contentBrief={pageData.content_brief}
                manifest={manifest}
                onChange={handleDataChange}
                siteId={pageData.site || parseInt(siteId || '0')}
                pageId={pageData.id}
                currentParent={pageData.parent}
              />
            </TabPanel>
          </Tabs>
        )}
      </Box>

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcuts
        open={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />

      {/* AI Chat Assistant */}
      <AIChatAssistant
        context={{
          pageId: pageId,
          siteId: siteId,
          currentPage: pageData?.slug,
        }}
      />

      {/* Success Message */}
      <SuccessMessage
        open={showSuccessMessage}
        onClose={() => setShowSuccessMessage(false)}
        title={successMessage.title}
        message={successMessage.message}
      />

      {/* Section Editor Modal (for visual editor mode) */}
      {editingSectionIndex !== null && pageData.sections_draft?.[editingSectionIndex] && manifest && (
        <SectionEditor
          section={pageData.sections_draft[editingSectionIndex]}
          sectionConfig={manifest?.sections?.[pageData.sections_draft[editingSectionIndex].type]}
          onSave={(data) => {
            const newSections = [...(pageData.sections_draft || [])];
            newSections[editingSectionIndex] = {
              ...newSections[editingSectionIndex],
              ...data,
            };
            handleDataChange('sections', newSections);
            setEditingSectionIndex(null);
          }}
          onClose={() => setEditingSectionIndex(null)}
          aiSettings={pageData.ai_generation_settings}
          contentBrief={pageData.content_brief}
          siteId={pageData.site}
        />
      )}
    </Box>
  );
};